import { useState, useEffect, useMemo, useRef } from "react";
import DraggableModal from "./DraggableModal";
import { supabase } from "../supabaseClient";

/* ============================================================================
 * USDA Organic Operations Lookup — read-only search over `usda_operations`.
 *
 * Two paging modes drive the data flow:
 *
 *   SERVER-SIDE (default) — when only SQL-friendly filters are active
 *   (search, status, certifier). Each page is a single Supabase request
 *   that returns just the rows for the visible window plus the exact
 *   total count. Snappy at 4,912+ rows.
 *
 *   CLIENT-SIDE — when any jsonb-derived filter is active (city, state,
 *   product). Supabase can't ILIKE inside jsonb cleanly, so we pull the
 *   whole filtered superset in 1k-row batches and do filter + sort +
 *   page-slice locally.
 *
 * Sorting on city / state degrades gracefully in server mode (server
 * falls back to operation_name); client mode handles them properly via
 * extractAddr. The header click is enabled for every column either way.
 * ========================================================================= */

const T = {
  en: {
    title: "USDA Organic Lookup",
    subtitle: "Search certified operations from the NOP Integrity Database",
    searchPlaceholder: "Search by name, NOP ID or Client ID…",
    filtersBtn: "Filters",
    filterStatus: "Status",
    filterCertifier: "Certifier",
    filterState: "State",
    filterCity: "City",
    filterProduct: "Product",
    filterAll: "All",
    filterAllStatuses: "All statuses",
    filterAllCertifiers: "All certifiers",
    filterAllStates: "All states",
    filterCityPh: "City (substring)…",
    filterProductPh: "Product / variety…",
    filterClearAll: "Clear all",
    statusCertified: "Certified",
    statusSurrendered: "Surrendered",
    statusSuspended: "Suspended",
    statusRevoked: "Revoked",
    colName: "Operation",
    colClientId: "Client ID",
    colNopId: "NOP ID",
    colCertifier: "Certifier",
    colCity: "City",
    colState: "State",
    colCountry: "Country",
    colStatus: "Status",
    colAnniversary: "Anniversary",
    loading: "Loading…",
    empty: "No operations match your search.",
    emptyHint: "Try widening the search or clearing the filters.",
    resultsCount: "results",
    rowsPerPage: "Rows per page:",
    rowsAll: "All",
    page: "Page",
    of: "of",
    detailTitle: "USDA Operation Detail",
    labelNopId: "NOP Operation ID",
    labelClientId: "Client Operation ID",
    labelStatus: "Status",
    labelStatusEffective: "Status effective",
    labelAnniversary: "Anniversary date",
    labelCountry: "Country code",
    labelCertifier: "Certifier",
    sectionIdentity: "Identity",
    sectionContact: "Contact",
    labelContact: "Contact name",
    labelPhone: "Phone",
    labelEmail: "Email",
    sectionScopes: "Scopes",
    sectionAddresses: "Addresses",
    sectionProducts: "Certified Products",
    addressPhysical: "Physical address",
    addressMailing: "Mailing address",
    viewInUsda: "Open in USDA Integrity",
    syncedAt: "Last synced",
    fetchError: "Could not load operations.",
    noItems: "No items recorded yet — sync may be in progress.",
    colProduct: "Product",
    colScope: "Scope",
    colItemStatus: "Status",
    colItemEffective: "Effective",
    colVarieties: "Varieties",
  },
  es: {
    title: "Consulta USDA Orgánico",
    subtitle: "Busca operaciones certificadas en la base NOP Integrity",
    searchPlaceholder: "Buscar por nombre, NOP ID o Client ID…",
    filtersBtn: "Filtros",
    filterStatus: "Estatus",
    filterCertifier: "Certificadora",
    filterState: "Estado",
    filterCity: "Ciudad",
    filterProduct: "Producto",
    filterAll: "Todos",
    filterAllStatuses: "Todos los estatus",
    filterAllCertifiers: "Todas las certificadoras",
    filterAllStates: "Todos los estados",
    filterCityPh: "Ciudad (subcadena)…",
    filterProductPh: "Producto / variedad…",
    filterClearAll: "Limpiar todo",
    statusCertified: "Certificado",
    statusSurrendered: "Renunciado",
    statusSuspended: "Suspendido",
    statusRevoked: "Revocado",
    colName: "Operación",
    colClientId: "Client ID",
    colNopId: "NOP ID",
    colCertifier: "Certificadora",
    colCity: "Ciudad",
    colState: "Estado",
    colCountry: "País",
    colStatus: "Estatus",
    colAnniversary: "Aniversario",
    loading: "Cargando…",
    empty: "Ninguna operación coincide con la búsqueda.",
    emptyHint: "Amplía la búsqueda o limpia los filtros.",
    resultsCount: "resultados",
    rowsPerPage: "Filas por página:",
    rowsAll: "Todos",
    page: "Página",
    of: "de",
    detailTitle: "Detalle de Operación USDA",
    labelNopId: "NOP Operation ID",
    labelClientId: "Client Operation ID",
    labelStatus: "Estatus",
    labelStatusEffective: "Estatus efectivo",
    labelAnniversary: "Fecha aniversario",
    labelCountry: "Código de país",
    labelCertifier: "Certificadora",
    sectionIdentity: "Identidad",
    sectionContact: "Contacto",
    labelContact: "Nombre del contacto",
    labelPhone: "Teléfono",
    labelEmail: "Correo electrónico",
    sectionScopes: "Alcances (Scopes)",
    sectionAddresses: "Direcciones",
    sectionProducts: "Productos Certificados",
    addressPhysical: "Dirección física",
    addressMailing: "Dirección postal",
    viewInUsda: "Ver en USDA Integrity",
    syncedAt: "Última sincronización",
    fetchError: "No se pudieron cargar las operaciones.",
    noItems: "No hay productos registrados — el sync puede estar en proceso.",
    colProduct: "Producto",
    colScope: "Alcance",
    colItemStatus: "Estatus",
    colItemEffective: "Efectivo",
    colVarieties: "Variedades",
  },
};

const PAGE_SIZE_OPTIONS = [20, 50, 100, 200, "all"];
const BATCH_SIZE = 1000;

/* 32 Mexican entities — the table is MX-only by design. Keeping the
   list inline avoids a second round-trip just to populate the filter
   dropdown and keeps things consistent when the underlying data is
   spotty (some addresses spell states differently). */
const MX_STATES = [
  "Aguascalientes", "Baja California", "Baja California Sur", "Campeche",
  "Chiapas", "Chihuahua", "Ciudad de México", "Coahuila", "Colima",
  "Durango", "Guanajuato", "Guerrero", "Hidalgo", "Jalisco", "México",
  "Michoacán", "Morelos", "Nayarit", "Nuevo León", "Oaxaca", "Puebla",
  "Querétaro", "Quintana Roo", "San Luis Potosí", "Sinaloa", "Sonora",
  "Tabasco", "Tamaulipas", "Tlaxcala", "Veracruz", "Yucatán", "Zacatecas",
];

function statusColors(status) {
  const key = String(status || "").toLowerCase();
  if (key === "certified") return { bg: "rgba(16,185,129,0.12)", color: "#047857", border: "rgba(16,185,129,0.3)" };
  if (key === "surrendered") return { bg: "rgba(107,114,128,0.12)", color: "#374151", border: "rgba(107,114,128,0.3)" };
  if (key === "suspended") return { bg: "rgba(245,158,11,0.15)", color: "#92400E", border: "rgba(245,158,11,0.3)" };
  if (key === "revoked") return { bg: "rgba(220,38,38,0.12)", color: "#991B1B", border: "rgba(220,38,38,0.3)" };
  return { bg: "rgba(107,114,128,0.08)", color: "#6B7280", border: "rgba(107,114,128,0.2)" };
}

function fmtDate(d) {
  if (!d) return "";
  try {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return String(d);
    return dt.toISOString().slice(0, 10);
  } catch (e) { return String(d); }
}

function fmtDateTime(d) {
  if (!d) return "";
  try {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return String(d);
    return dt.toISOString().slice(0, 16).replace("T", " ") + " UTC";
  } catch (e) { return String(d); }
}

/* Pull city + state from the addresses jsonb. Prefers Physical, falls
   back to Mailing, then the first address present. */
function extractAddr(addresses) {
  const arr = Array.isArray(addresses) ? addresses : [];
  const phys = arr.find(function (a) { return a && a.addressType === "Physical"; });
  const mail = arr.find(function (a) { return a && a.addressType === "Mailing"; });
  const a = phys || mail || arr[0] || {};
  return { city: a.city || "", state: a.stateOrProvince || "" };
}

export default function USDALookup({ lang }) {
  const t = T[lang] || T.es;

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [filterCertifier, setFilterCertifier] = useState("");
  const [filterState, setFilterState] = useState("");
  const [filterCity, setFilterCity] = useState("");
  const [filterProduct, setFilterProduct] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [certifierOptions, setCertifierOptions] = useState([]);
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState("operation_name");
  const [sortDir, setSortDir] = useState("asc");
  const [selectedId, setSelectedId] = useState(null);

  /* Debounce the typed search so each keystroke doesn't fire a
     Supabase round-trip. 250 ms feels live. */
  const [searchDebounced, setSearchDebounced] = useState("");
  useEffect(function () {
    const id = setTimeout(function () { setSearchDebounced(search.trim()); }, 250);
    return function () { clearTimeout(id); };
  }, [search]);

  /* Pre-load distinct certifier names once on mount so the filter
     dropdown has something to show without scanning every page. */
  useEffect(function () {
    let cancelled = false;
    (async function () {
      try {
        const { data } = await supabase
          .from("usda_operations")
          .select("certifier_name")
          .not("certifier_name", "is", null);
        if (cancelled) return;
        const unique = Array.from(new Set((data || []).map(function (r) { return r.certifier_name; }).filter(Boolean))).sort();
        setCertifierOptions(unique);
      } catch (e) { /* swallow — dropdown just stays empty */ }
    })();
    return function () { cancelled = true; };
  }, []);

  /* The presence of any jsonb-derived filter forces us off the
     server-side paging path because Postgrest can't ILIKE into jsonb
     cleanly. Recomputed every render but cheap. */
  const isClientSideMode = !!(filterCity || filterState || filterProduct);

  /* Reset to page 1 whenever any filter / sort / page-size changes.
     Sort changes count here too — paginating into a re-sorted set is
     never what the user wants. */
  const lastSigRef = useRef("");
  const resetSig = JSON.stringify([searchDebounced, statusFilter, filterCertifier, filterCity, filterState, filterProduct, sortBy, sortDir, pageSize]);
  useEffect(function () {
    if (lastSigRef.current !== resetSig) {
      lastSigRef.current = resetSig;
      setCurrentPage(1);
    }
  }, [resetSig]);

  /* ── Server-side: one query, one page, exact count. ────────────────
     When pageSize === "all" we still need everything, so we loop in
     1k batches keeping the first response's count. The server sort
     falls back to operation_name when the user picked city / state
     (Postgrest can't sort by a jsonb child path on this table). */
  async function fetchPageServerSide() {
    setLoading(true);
    setError("");
    try {
      let q = supabase
        .from("usda_operations")
        .select("nop_operation_id, operation_name, client_operation_id, operation_status, status_effective_date, anniversary_date, country_code, addresses, certifier_name, items", { count: "exact" });

      if (searchDebounced) {
        const pat = "%" + searchDebounced.replace(/[\\%_]/g, "\\$&") + "%";
        q = q.or("operation_name.ilike." + pat + ",nop_operation_id.ilike." + pat + ",client_operation_id.ilike." + pat);
      }
      if (statusFilter) q = q.eq("operation_status", statusFilter);
      if (filterCertifier) q = q.eq("certifier_name", filterCertifier);

      const serverSortField = (sortBy === "city" || sortBy === "state") ? "operation_name" : sortBy;
      q = q.order(serverSortField, { ascending: sortDir === "asc" });

      if (pageSize === "all") {
        const all = [];
        let from = 0;
        let totalCount = 0;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { data, count, error: e } = await q.range(from, from + BATCH_SIZE - 1);
          if (e) throw e;
          if (count !== null && count !== undefined) totalCount = count;
          if (!data || data.length === 0) break;
          all.push.apply(all, data);
          if (data.length < BATCH_SIZE) break;
          from += BATCH_SIZE;
        }
        setRows(all);
        setTotal(totalCount || all.length);
      } else {
        const start = (currentPage - 1) * pageSize;
        const end = start + pageSize - 1;
        const { data, count, error: e } = await q.range(start, end);
        if (e) throw e;
        setRows(data || []);
        setTotal(count || 0);
      }
    } catch (err) {
      setError((err && err.message) ? err.message : String(err));
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  /* ── Client-side mode: pull the full SQL-filtered superset in 1k
     batches, then let displayRows + clientFilteredCount do the work. */
  async function fetchAllForClientSide() {
    setLoading(true);
    setError("");
    try {
      let baseQuery = supabase
        .from("usda_operations")
        .select("nop_operation_id, operation_name, client_operation_id, operation_status, status_effective_date, anniversary_date, country_code, addresses, certifier_name, items");

      if (searchDebounced) {
        const pat = "%" + searchDebounced.replace(/[\\%_]/g, "\\$&") + "%";
        baseQuery = baseQuery.or("operation_name.ilike." + pat + ",nop_operation_id.ilike." + pat + ",client_operation_id.ilike." + pat);
      }
      if (statusFilter) baseQuery = baseQuery.eq("operation_status", statusFilter);
      if (filterCertifier) baseQuery = baseQuery.eq("certifier_name", filterCertifier);

      const all = [];
      let from = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { data, error: e } = await baseQuery.range(from, from + BATCH_SIZE - 1);
        if (e) throw e;
        if (!data || data.length === 0) break;
        all.push.apply(all, data);
        if (data.length < BATCH_SIZE) break;
        from += BATCH_SIZE;
      }
      setRows(all);
    } catch (err) {
      setError((err && err.message) ? err.message : String(err));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  /* Single hook routes to the right fetcher whenever any input
     changes. Both server-side and client-side mode re-run when
     anything in this dep list changes; the gate inside picks which
     function to call. */
  useEffect(function () {
    if (isClientSideMode) {
      fetchAllForClientSide();
    } else {
      fetchPageServerSide();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDebounced, statusFilter, filterCertifier, sortBy, sortDir, pageSize, currentPage, isClientSideMode, filterCity, filterState, filterProduct]);

  /* ── Display + total derivations ───────────────────────────────────
     In server-side mode, rows is already the page slice; just hand it
     through. In client-side mode, apply the three jsonb filters, sort
     by the chosen column, then slice into the visible window. */
  const displayRows = useMemo(function () {
    if (!isClientSideMode) return rows;

    let arr = rows.slice();

    if (filterCity) {
      const needle = filterCity.toLowerCase();
      arr = arr.filter(function (r) {
        const addrs = Array.isArray(r.addresses) ? r.addresses : [];
        return addrs.some(function (a) { return ((a && a.city) || "").toLowerCase().includes(needle); });
      });
    }
    if (filterState) {
      const needle = filterState.toLowerCase();
      arr = arr.filter(function (r) {
        const addrs = Array.isArray(r.addresses) ? r.addresses : [];
        return addrs.some(function (a) { return ((a && a.stateOrProvince) || "").toLowerCase().includes(needle); });
      });
    }
    if (filterProduct) {
      const needle = filterProduct.toLowerCase();
      arr = arr.filter(function (r) {
        const items = Array.isArray(r.items) ? r.items : [];
        return items.some(function (it) {
          const blob = [it.itemName, it.otherItems, it.categoryName, it.itemVarieties].filter(Boolean).join(" ").toLowerCase();
          return blob.includes(needle);
        });
      });
    }

    arr.sort(function (a, b) {
      function getVal(r) {
        if (sortBy === "city" || sortBy === "state") {
          const addrs = Array.isArray(r.addresses) ? r.addresses : [];
          const phys = addrs.find(function (x) { return x && x.addressType === "Physical"; });
          const mail = addrs.find(function (x) { return x && x.addressType === "Mailing"; });
          const ad = phys || mail || addrs[0] || {};
          return ((sortBy === "city" ? ad.city : ad.stateOrProvince) || "").toLowerCase();
        }
        const v = r[sortBy];
        if (v == null) return "";
        return typeof v === "string" ? v.toLowerCase() : v;
      }
      const va = getVal(a);
      const vb = getVal(b);
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

    if (pageSize === "all") return arr;
    const start = (currentPage - 1) * pageSize;
    return arr.slice(start, start + pageSize);
  }, [rows, filterCity, filterState, filterProduct, sortBy, sortDir, pageSize, currentPage, isClientSideMode]);

  /* Total count for the header strip + pagination. In server mode
     this is just `total` (exact from Postgrest). In client mode it's
     the size of the filtered set BEFORE slicing — we re-run the
     filter chain here so the numbers stay in sync with displayRows. */
  const clientFilteredCount = useMemo(function () {
    if (!isClientSideMode) return total;
    let arr = rows;
    if (filterCity) {
      const needle = filterCity.toLowerCase();
      arr = arr.filter(function (r) {
        const addrs = Array.isArray(r.addresses) ? r.addresses : [];
        return addrs.some(function (a) { return ((a && a.city) || "").toLowerCase().includes(needle); });
      });
    }
    if (filterState) {
      const needle = filterState.toLowerCase();
      arr = arr.filter(function (r) {
        const addrs = Array.isArray(r.addresses) ? r.addresses : [];
        return addrs.some(function (a) { return ((a && a.stateOrProvince) || "").toLowerCase().includes(needle); });
      });
    }
    if (filterProduct) {
      const needle = filterProduct.toLowerCase();
      arr = arr.filter(function (r) {
        const items = Array.isArray(r.items) ? r.items : [];
        return items.some(function (it) {
          const blob = [it.itemName, it.otherItems, it.categoryName, it.itemVarieties].filter(Boolean).join(" ").toLowerCase();
          return blob.includes(needle);
        });
      });
    }
    return arr.length;
  }, [isClientSideMode, total, rows, filterCity, filterState, filterProduct]);

  const displayTotal = clientFilteredCount;
  const totalPages = pageSize === "all" ? 1 : Math.max(1, Math.ceil(displayTotal / pageSize));

  const statusOptions = useMemo(function () {
    return [
      { value: "", label: t.filterAllStatuses },
      { value: "Certified", label: t.statusCertified },
      { value: "Surrendered", label: t.statusSurrendered },
      { value: "Suspended", label: t.statusSuspended },
      { value: "Revoked", label: t.statusRevoked },
    ];
  }, [t]);

  const selectedOp = useMemo(function () {
    if (!selectedId) return null;
    return rows.find(function (r) { return r.nop_operation_id === selectedId; })
      || { nop_operation_id: selectedId };
  }, [selectedId, rows]);

  const activeFilterCount =
    (statusFilter ? 1 : 0) +
    (filterCertifier ? 1 : 0) +
    (filterState ? 1 : 0) +
    (filterCity ? 1 : 0) +
    (filterProduct ? 1 : 0);

  const clearAllFilters = function () {
    setStatusFilter("");
    setFilterCertifier("");
    setFilterState("");
    setFilterCity("");
    setFilterProduct("");
  };

  const toggleSort = function (colKey) {
    if (sortBy === colKey) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(colKey);
      setSortDir("asc");
    }
  };

  const inp = { padding: "8px 12px", borderRadius: 6, border: "1px solid var(--color-border)", background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none", fontFamily: "inherit", fontSize: 13 };
  const thBase = { padding: "10px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.04em", borderBottom: "2px solid var(--color-border)", whiteSpace: "nowrap", background: "var(--color-bg-card-solid)", position: "sticky", top: 0, zIndex: 1, cursor: "pointer", userSelect: "none" };
  const td = { padding: "8px 12px", fontSize: 12, borderBottom: "1px solid var(--color-border)", whiteSpace: "nowrap" };

  const renderTh = function (key, label) {
    const active = sortBy === key;
    const arrow = active ? (sortDir === "asc" ? "▲" : "▼") : "";
    return (
      <th
        key={key}
        style={{ ...thBase, color: active ? "var(--color-accent)" : thBase.color }}
        onClick={function () { toggleSort(key); }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          {label}
          {arrow && <span style={{ fontSize: 9 }}>{arrow}</span>}
        </span>
      </th>
    );
  };

  return (
    <div style={{ padding: "20px 24px" }}>
      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "var(--color-text)" }}>{t.title}</h1>
        <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--color-text-muted)" }}>{t.subtitle}</p>
      </div>

      {/* Toolbar */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 14, padding: "12px 14px", background: "var(--color-bg-card-solid)", borderRadius: 8, border: "1px solid var(--color-border)", position: "relative" }}>
        <input
          type="text"
          value={search}
          onChange={function (e) { setSearch(e.target.value); }}
          placeholder={t.searchPlaceholder}
          style={{ ...inp, flex: 1, minWidth: 280 }}
        />
        <button
          type="button"
          onClick={function () { setShowFilters(function (v) { return !v; }); }}
          style={{ padding: "8px 14px", borderRadius: 6, border: "1px solid var(--color-border)", background: showFilters || activeFilterCount > 0 ? "var(--color-accent-bg)" : "var(--color-bg-card-solid)", color: showFilters || activeFilterCount > 0 ? "var(--color-accent)" : "var(--color-text)", cursor: "pointer", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}
        >
          <span style={{ fontSize: 14, lineHeight: 1 }}>⋮</span>
          <span>{t.filtersBtn}</span>
          {activeFilterCount > 0 && (
            <span style={{ minWidth: 18, height: 18, padding: "0 5px", borderRadius: 9, background: "var(--color-accent)", color: "#fff", fontSize: 10, fontWeight: 700, lineHeight: "18px", textAlign: "center" }}>{activeFilterCount}</span>
          )}
        </button>
        <div style={{ marginLeft: "auto", fontSize: 11, color: "var(--color-text-muted)" }}>
          {loading ? t.loading : displayTotal.toLocaleString() + " " + t.resultsCount}
        </div>

        {/* Filter popover */}
        {showFilters && (
          <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 14, zIndex: 20, background: "var(--color-bg-card-solid)", border: "1px solid var(--color-border)", borderRadius: 8, padding: 16, minWidth: 360, boxShadow: "0 12px 32px rgba(0,0,0,0.18)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--color-text)" }}>{t.filtersBtn}</div>
              {activeFilterCount > 0 && (
                <button type="button" onClick={clearAllFilters} style={{ background: "transparent", border: "none", color: "var(--color-accent)", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>{t.filterClearAll}</button>
              )}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <FilterField label={t.filterStatus}>
                <select value={statusFilter} onChange={function (e) { setStatusFilter(e.target.value); }} style={{ ...inp, width: "100%", cursor: "pointer" }}>
                  {statusOptions.map(function (o) { return <option key={o.value} value={o.value}>{o.label}</option>; })}
                </select>
              </FilterField>
              <FilterField label={t.filterCertifier}>
                <select value={filterCertifier} onChange={function (e) { setFilterCertifier(e.target.value); }} style={{ ...inp, width: "100%", cursor: "pointer" }}>
                  <option value="">{t.filterAllCertifiers}</option>
                  {certifierOptions.map(function (c) { return <option key={c} value={c}>{c}</option>; })}
                </select>
              </FilterField>
              <FilterField label={t.filterState}>
                <select value={filterState} onChange={function (e) { setFilterState(e.target.value); }} style={{ ...inp, width: "100%", cursor: "pointer" }}>
                  <option value="">{t.filterAllStates}</option>
                  {MX_STATES.map(function (s) { return <option key={s} value={s}>{s}</option>; })}
                </select>
              </FilterField>
              <FilterField label={t.filterCity}>
                <input type="text" value={filterCity} onChange={function (e) { setFilterCity(e.target.value); }} placeholder={t.filterCityPh} style={{ ...inp, width: "100%" }} />
              </FilterField>
              <FilterField label={t.filterProduct} full>
                <input type="text" value={filterProduct} onChange={function (e) { setFilterProduct(e.target.value); }} placeholder={t.filterProductPh} style={{ ...inp, width: "100%" }} />
              </FilterField>
            </div>
          </div>
        )}
      </div>

      {/* Error banner */}
      {error && (
        <div style={{ padding: 10, marginBottom: 12, background: "#FEE2E2", color: "#991B1B", border: "1px solid #FCA5A5", borderRadius: 6, fontSize: 12 }}>
          {t.fetchError} <span style={{ opacity: 0.75 }}>{error}</span>
        </div>
      )}

      {/* Table */}
      <div style={{ background: "var(--color-bg-card-solid)", borderRadius: 8, border: "1px solid var(--color-border)", overflow: "hidden" }}>
        {!loading && displayRows.length === 0 ? (
          <div style={{ padding: "40px 24px", textAlign: "center", color: "var(--color-text-muted)" }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{t.empty}</div>
            <div style={{ fontSize: 12 }}>{t.emptyHint}</div>
          </div>
        ) : (
          <div style={{ overflowY: "auto", overflowX: "auto", maxHeight: "60vh" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {renderTh("operation_name", t.colName)}
                  {renderTh("client_operation_id", t.colClientId)}
                  {renderTh("nop_operation_id", t.colNopId)}
                  {renderTh("certifier_name", t.colCertifier)}
                  {renderTh("city", t.colCity)}
                  {renderTh("state", t.colState)}
                  {renderTh("country_code", t.colCountry)}
                  {renderTh("operation_status", t.colStatus)}
                  {renderTh("anniversary_date", t.colAnniversary)}
                </tr>
              </thead>
              <tbody>
                {displayRows.map(function (r) {
                  const sc = statusColors(r.operation_status);
                  const addr = extractAddr(r.addresses);
                  return (
                    <tr
                      key={r.nop_operation_id}
                      onClick={function () { setSelectedId(r.nop_operation_id); }}
                      style={{ cursor: "pointer", background: "transparent" }}
                      onMouseEnter={function (e) { e.currentTarget.style.background = "var(--color-bg-hover)"; }}
                      onMouseLeave={function (e) { e.currentTarget.style.background = "transparent"; }}
                    >
                      <td style={{ ...td, fontWeight: 600, color: "var(--color-text)" }}>{r.operation_name || "—"}</td>
                      <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--color-text-muted)" }}>{r.client_operation_id || "—"}</td>
                      <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--color-text-muted)" }}>{r.nop_operation_id}</td>
                      <td style={td}>{r.certifier_name || "—"}</td>
                      <td style={td}>{addr.city || "—"}</td>
                      <td style={td}>{addr.state || "—"}</td>
                      <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace" }}>{r.country_code || "—"}</td>
                      <td style={td}>
                        <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 700, background: sc.bg, color: sc.color, border: "1px solid " + sc.border }}>
                          {r.operation_status || "—"}
                        </span>
                      </td>
                      <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", color: "var(--color-text-muted)" }}>{fmtDate(r.anniversary_date) || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {loading && displayRows.length === 0 && (
          <div style={{ padding: 40, textAlign: "center", color: "var(--color-text-muted)", fontSize: 13 }}>{t.loading}</div>
        )}
      </div>

      {/* Pagination bar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
          <span>{t.rowsPerPage}</span>
          <select
            value={pageSize === "all" ? "all" : String(pageSize)}
            onChange={function (e) {
              const v = e.target.value;
              setPageSize(v === "all" ? "all" : parseInt(v, 10));
              setCurrentPage(1);
            }}
            style={{ padding: "4px 8px", borderRadius: 6, border: "1px solid var(--color-border)", background: "var(--color-bg-input)", color: "var(--color-text)", fontSize: 12, cursor: "pointer" }}
          >
            {PAGE_SIZE_OPTIONS.map(function (opt) {
              return <option key={String(opt)} value={String(opt)}>{opt === "all" ? t.rowsAll : opt}</option>;
            })}
          </select>
          <span style={{ color: "var(--color-text-muted)" }}>{displayTotal.toLocaleString()} {t.resultsCount}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            type="button"
            onClick={function () { setCurrentPage(function (p) { return Math.max(1, p - 1); }); }}
            disabled={currentPage === 1 || pageSize === "all"}
            style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid var(--color-border)", background: "var(--color-bg-card-solid)", cursor: (currentPage === 1 || pageSize === "all") ? "not-allowed" : "pointer", opacity: (currentPage === 1 || pageSize === "all") ? 0.5 : 1 }}
            aria-label="Previous page"
          >←</button>
          <span style={{ fontSize: 12 }}>{t.page} {currentPage} {t.of} {totalPages}</span>
          <button
            type="button"
            onClick={function () { setCurrentPage(function (p) { return Math.min(totalPages, p + 1); }); }}
            disabled={currentPage === totalPages || pageSize === "all"}
            style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid var(--color-border)", background: "var(--color-bg-card-solid)", cursor: (currentPage === totalPages || pageSize === "all") ? "not-allowed" : "pointer", opacity: (currentPage === totalPages || pageSize === "all") ? 0.5 : 1 }}
            aria-label="Next page"
          >→</button>
        </div>
      </div>

      {selectedOp && (
        <USDAOperationDetail
          nopId={selectedOp.nop_operation_id}
          lang={lang}
          t={t}
          onClose={function () { setSelectedId(null); }}
        />
      )}
    </div>
  );
}

function FilterField({ label, full, children }) {
  return (
    <div style={{ gridColumn: full ? "1 / -1" : "auto" }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

/* Detail modal — fetches the full row (including jsonb fields) for
   the selected operation, including items so the Products section
   below can render whatever the sync job last captured. */
function USDAOperationDetail({ nopId, lang, t, onClose }) {
  const [op, setOp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(function () {
    let cancelled = false;
    (async function () {
      setLoading(true);
      setError("");
      try {
        const { data, error: e } = await supabase
          .from("usda_operations")
          .select("*")
          .eq("nop_operation_id", nopId)
          .maybeSingle();
        if (e) throw e;
        if (!cancelled) setOp(data);
      } catch (err) {
        if (!cancelled) setError(err && err.message ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return function () { cancelled = true; };
  }, [nopId]);

  const sc = op ? statusColors(op.operation_status) : statusColors("");
  const usdaUrl = "https://organic.ams.usda.gov/Integrity/CP/OPP?nopid=" + encodeURIComponent(nopId);

  return (
    <DraggableModal title={t.detailTitle} onClose={onClose}>
      <div style={{ padding: 16, flex: 1, overflowY: "auto" }}>
        {loading && <div style={{ padding: 24, textAlign: "center", color: "var(--color-text-muted)" }}>{t.loading}</div>}
        {error && <div style={{ padding: 10, background: "#FEE2E2", color: "#991B1B", border: "1px solid #FCA5A5", borderRadius: 6 }}>{error}</div>}
        {op && (
          <div>
            {/* Header with name + status badge */}
            <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid var(--color-border)" }}>
              <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>{op.operation_name || "—"}</div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 10, fontSize: 11, fontWeight: 700, background: sc.bg, color: sc.color, border: "1px solid " + sc.border }}>
                  {op.operation_status || "—"}
                </span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--color-text-muted)" }}>{op.nop_operation_id}</span>
              </div>
            </div>

            {/* Identity */}
            <Section title={t.sectionIdentity}>
              <Field label={t.labelNopId} value={op.nop_operation_id} mono />
              <Field label={t.labelClientId} value={op.client_operation_id} mono />
              <Field label={t.labelCertifier} value={op.certifier_name} />
              <Field label={t.labelStatus} value={op.operation_status} />
              <Field label={t.labelStatusEffective} value={fmtDate(op.status_effective_date)} mono />
              <Field label={t.labelAnniversary} value={fmtDate(op.anniversary_date)} mono />
              <Field label={t.labelCountry} value={op.country_code} mono />
            </Section>

            {/* Contact */}
            <Section title={t.sectionContact}>
              <Field label={t.labelContact} value={[op.contact_first_name, op.contact_last_name].filter(Boolean).join(" ")} />
              <Field label={t.labelPhone} value={op.phone} mono />
              <Field label={t.labelEmail} value={op.email} />
            </Section>

            {/* Scopes */}
            <Section title={t.sectionScopes}>
              {(op.scopes || []).length === 0 && <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>—</div>}
              {(op.scopes || []).map(function (sc2, i) {
                const sci = statusColors(sc2.nopScopeStatus);
                return (
                  <div key={i} style={{ display: "flex", gap: 12, alignItems: "center", padding: "6px 0", borderBottom: i < (op.scopes || []).length - 1 ? "1px dashed var(--color-border)" : "none" }}>
                    <div style={{ fontWeight: 600, fontSize: 12, minWidth: 120 }}>{sc2.nopScope || "—"}</div>
                    <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 700, background: sci.bg, color: sci.color, border: "1px solid " + sci.border }}>{sc2.nopScopeStatus || "—"}</span>
                    <div style={{ fontSize: 11, color: "var(--color-text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>{fmtDate(sc2.statusEffectiveDate)}</div>
                  </div>
                );
              })}
            </Section>

            {/* Addresses */}
            <Section title={t.sectionAddresses}>
              {(op.addresses || []).length === 0 && <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>—</div>}
              {(op.addresses || []).map(function (addr, i) {
                const label = addr.addressType === "Physical" ? t.addressPhysical : addr.addressType === "Mailing" ? t.addressMailing : (addr.addressType || "—");
                return (
                  <div key={i} style={{ marginBottom: 8, padding: 8, background: "var(--color-bg)", borderRadius: 6, fontSize: 12 }}>
                    <div style={{ fontWeight: 700, fontSize: 11, color: "var(--color-text-muted)", textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
                    <div>{addr.addressLine1 || ""}{addr.addressLine2 ? ", " + addr.addressLine2 : ""}</div>
                    <div>{[addr.city, addr.stateOrProvince, addr.zipOrPostalCode].filter(Boolean).join(", ")}</div>
                    <div style={{ color: "var(--color-text-muted)" }}>{addr.countryCode3 || ""}</div>
                  </div>
                );
              })}
            </Section>

            {/* Certified Products */}
            <Section title={t.sectionProducts}>
              {(!op.items || op.items.length === 0) ? (
                <div style={{ fontSize: 12, color: "var(--color-text-muted)", fontStyle: "italic" }}>{t.noItems}</div>
              ) : (
                <div style={{ overflowX: "auto", border: "1px solid var(--color-border)", borderRadius: 6 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr>
                        <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 10, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", borderBottom: "2px solid var(--color-border)", background: "var(--color-bg)" }}>{t.colProduct}</th>
                        <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 10, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", borderBottom: "2px solid var(--color-border)", background: "var(--color-bg)" }}>{t.colScope}</th>
                        <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 10, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", borderBottom: "2px solid var(--color-border)", background: "var(--color-bg)" }}>{t.colItemStatus}</th>
                        <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 10, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", borderBottom: "2px solid var(--color-border)", background: "var(--color-bg)" }}>{t.colItemEffective}</th>
                        <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 10, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", borderBottom: "2px solid var(--color-border)", background: "var(--color-bg)" }}>{t.colVarieties}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {op.items.map(function (it, i) {
                        const displayName = (function () {
                          const name = String(it.itemName || "").trim();
                          const other = String(it.otherItems || "").trim();
                          const cat = String(it.categoryName || "").trim();
                          /* When USDA puts "Other" in itemName, the real product list lives
                             in otherItems as a comma-separated string. Fall back to category
                             when neither is informative. */
                          if (name && name.toLowerCase() !== "other") return name;
                          if (other) return other;
                          if (cat && cat.toLowerCase() !== "other") return cat;
                          return name || "—";
                        })();
                        const sci = statusColors(it.status);
                        const varieties = Array.isArray(it.itemVarieties)
                          ? it.itemVarieties.map(function (v) { return (v && (v.varietyName || v.name)) || v; }).filter(Boolean).join(", ")
                          : (it.itemVarieties || "");
                        return (
                          <tr key={i} style={{ borderBottom: "1px solid var(--color-border)" }}>
                            <td style={{ padding: "8px 10px", fontWeight: 600 }}>{displayName}</td>
                            <td style={{ padding: "8px 10px", color: "var(--color-text-muted)" }}>{it.scope || it.nopScope || "—"}</td>
                            <td style={{ padding: "8px 10px" }}>
                              <span style={{ display: "inline-block", padding: "2px 7px", borderRadius: 10, fontSize: 10, fontWeight: 700, background: sci.bg, color: sci.color, border: "1px solid " + sci.border }}>{it.status || "—"}</span>
                            </td>
                            <td style={{ padding: "8px 10px", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--color-text-muted)" }}>{fmtDate(it.statusEffectiveDate) || "—"}</td>
                            <td style={{ padding: "8px 10px", color: "var(--color-text-secondary)" }}>{varieties || "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>

            {/* Footer with link to USDA + sync info */}
            <div style={{ marginTop: 20, paddingTop: 12, borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <a href={usdaUrl} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 6, background: "#1F4E79", color: "#fff", textDecoration: "none", fontSize: 12, fontWeight: 700 }}>
                {t.viewInUsda}
                <span style={{ fontSize: 14 }}>↗</span>
              </a>
              <div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{t.syncedAt}: {fmtDateTime(op.synced_at)}</div>
            </div>
          </div>
        )}
      </div>
    </DraggableModal>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>{title}</div>
      <div>{children}</div>
    </div>
  );
}

function Field({ label, value, mono }) {
  if (!value && value !== 0) value = "—";
  return (
    <div style={{ display: "flex", gap: 12, padding: "4px 0", fontSize: 12 }}>
      <div style={{ minWidth: 200, color: "var(--color-text-muted)", fontWeight: 600 }}>{label}:</div>
      <div style={{ fontFamily: mono ? "'JetBrains Mono', monospace" : "inherit", fontSize: mono ? 11 : 12 }}>{value}</div>
    </div>
  );
}
